import {
  Button,
  Footer,
  Modal,
  rem,
  Text,
  Flex,
  PasswordInput,
} from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { type SubmitHandler, useForm } from "react-hook-form";
import Select from "src/components/shared/_FormInputs/Select";
import Switch from "src/components/shared/_FormInputs/Switch";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useGetApi, useIsOnline, usePostApi } from "src/hooks";
import { useDispatch } from "src/redux/hooks";
import { setPreferences } from "src/redux/slices/preferencesSlice";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { Notification } from "src/utils/Notification";
import { useState } from "react";
import { getCurrentVersion } from "../../../utils";
import {
  Divider,
  RowSection,
  Section,
  SettingsSection,
  SystemSettingsContainer,
  VerticalDivider,
} from "./SystemSettings.styled";
import { type ISystemSettings } from "./SystemSettings.types";

const mapUserSettings = (data: any) =>
  data.data.content.settings as ISystemSettings;

const SystemSettings = () => {
  const [fileDownloading, setFileDownloading] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const isOnline = useIsOnline();
  const dispatch = useDispatch();

  const { data } = useGetApi({
    request: "get-settings",
    body: { params: { sect: "_usersettings" } },
    mapper: mapUserSettings,
  });

  const handleDownload = () => {
    //   const { data: file, isLoading } = useGetApi({
    //     request: "backup",
    // body: {
    //   params: {
    //     userPass: "yourUserPassword";
    //   }
    // }
    //   });
    //   isLoading !== undefined && setFileDownloading(isLoading);
    //   if (file) {
    //     console.log(file);
    //   } else {
    //     console.error("Failed to download file");
    //   }
  };

  const { post, isLoading } = usePostApi();

  const { handleSubmit, control, reset } = useForm<ISystemSettings>({
    values: data,
  });

  const onSubmit: SubmitHandler<ISystemSettings> = async data => {
    await post({
      request: "save-settings",
      body: { params: data },
      onSuccess: () => {
        reset(data);
        dispatch(
          setPreferences({
            theme: data.theme,
            logoutTime: data.logoutTime,
          }),
        );
        Notification(
          "Success",
          "Your settings have been saved successfuly!",
          "green",
        );
      },
    });
  };

  return (
    <PageContainer id="page-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Breadcrumbs>
          {isOnline && (
            <ButtonsWrapper>
              <Button
                leftIcon={<IconDeviceFloppy size={"1.4rem"} />}
                variant="outline"
                ml={5}
                type="submit"
                loading={isLoading}>
                Save
              </Button>
            </ButtonsWrapper>
          )}
        </Breadcrumbs>

        <SystemSettingsContainer>
          <SettingsSection>
            <Section>
              <RowSection>
                <Text size={"sm"} mr={"30px"}>
                  Theme
                </Text>
                <Select
                  name="theme"
                  control={control}
                  placeholder="Pick one"
                  readOnly={!isOnline}
                  data={[
                    { label: "Auto", value: "auto" },
                    { label: "Light", value: "day" },
                    { label: "Dark", value: "night" },
                  ]}
                />
                {/* <ThemeToggle control={control} disabled={!isOnline} /> */}
              </RowSection>
              <Divider />

              <RowSection>
                <Text size={"sm"} mr={"30px"}>
                  Auto Logout Time
                </Text>
                <Select
                  name="logoutTime"
                  control={control}
                  placeholder="Pick one"
                  readOnly={!isOnline}
                  data={[
                    { value: "5", label: "5 min" },
                    { value: "10", label: "10 min" },
                    { value: "15", label: "15 min" },
                    { value: "30", label: "30 min" },
                    { value: "45", label: "45 min" },
                    { value: "60", label: "60 min" },
                  ]}
                />
              </RowSection>
              <Divider />

              <RowSection>
                <Text size={"sm"}>Push Notifications</Text>
                <Switch name="push" control={control} disabled={!isOnline} />
              </RowSection>
              <Divider />
              {/* <Button
                onClick={() => setIsDownloadModalOpen(true)}
                disabled={!isOnline}>
                Download Database File
              </Button>
              <Modal
                opened={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}>
                <Flex gap="xs" direction="column">
                  <Text>Enter a password to protect your file. (optional)</Text>
                  <PasswordInput></PasswordInput>
                </Flex>
                <Flex gap="md" justify="flex-end" mt="md">
                  <Button onClick={() => setIsDownloadModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleDownload}>Download</Button>
                </Flex>
              </Modal> */}
            </Section>

            <VerticalDivider />

            <Section>
              <RowSection>
                <Text size={"sm"}>When an entry requires my verification</Text>
                <Switch
                  ml={50}
                  name="pushVerify"
                  control={control}
                  disabled={!isOnline}
                />
              </RowSection>

              <RowSection>
                <Text size={"sm"}>Frequency</Text>
                <Select
                  name="pushVerifyFreq"
                  control={control}
                  w={"50%"}
                  readOnly={!isOnline}
                  data={[
                    { value: "5", label: "Daily" },
                    { value: "10", label: "Every 3 Days" },
                    { value: "15", label: "Weekly on Monday" },
                    { value: "30", label: "Weekly on Friday" },
                  ]}
                />
              </RowSection>

              <Divider />

              <RowSection>
                <Text size={"sm"}>
                  When an entry requires my re-verification
                </Text>
                <Switch
                  ml={50}
                  name="pushReverify"
                  control={control}
                  disabled={!isOnline}
                />
              </RowSection>

              <RowSection>
                <Text size={"sm"}>Frequency</Text>
                <Select
                  name="pushReverifyFreq"
                  control={control}
                  w={"50%"}
                  readOnly={!isOnline}
                  data={[
                    { value: "5", label: "Daily" },
                    { value: "10", label: "Every 3 Days" },
                    { value: "15", label: "Weekly on Monday" },
                    { value: "30", label: "Weekly on Friday" },
                  ]}
                />
              </RowSection>

              <Divider />

              <RowSection>
                <Text size={"sm"}>When an entry requires my co-sign</Text>
                <Switch
                  ml={50}
                  name="pushCosign"
                  control={control}
                  disabled={!isOnline}
                />
              </RowSection>

              <RowSection>
                <Text size={"sm"}>Frequency</Text>
                <Select
                  name="pushCosignFreq"
                  control={control}
                  w={"50%"}
                  readOnly={!isOnline}
                  data={[
                    { value: "5", label: "Daily" },
                    { value: "10", label: "Every 3 Days" },
                    { value: "15", label: "Weekly on Monday" },
                    { value: "30", label: "Weekly on Friday" },
                  ]}
                />
              </RowSection>
            </Section>
          </SettingsSection>
        </SystemSettingsContainer>
      </form>
      <Footer
        height={60}
        style={{
          position: "absolute",
          bottom: "0",
          left: "0",
          padding: 10,
          display: "flex",
          alignItems: "center",
          fontSize: rem(12),
          zIndex: 99,
        }}>
        Version: {getCurrentVersion()}
      </Footer>
    </PageContainer>
  );
};

export default SystemSettings;
